import { CircleCheck, Loader2 } from "lucide-react";
import type { Control, FieldPath } from "react-hook-form";

import { DocumentosLabels } from "@/lib/labels/documentos";
import {
	SignaturePreparation,
	type SignaturePreparationForm,
} from "@/routes/_app/pessoa/-utils/signature-preparation";
import type { SignatureRequirement, SignatureTerm } from "@/types/signatures";

import { SendTermsFieldInput } from "./field-input";

export function SendTermsFields({
	terms,
	requirements,
	customValues,
	control,
	pending,
	disabled,
}: {
	terms: SignatureTerm[];
	requirements: SignatureRequirement[];
	customValues: Record<string, string | undefined>;
	control: Control<SignaturePreparationForm>;
	pending: boolean;
	disabled: boolean;
}) {
	if (terms.length === 0) {
		return null;
	}

	return (
		<section data-component="signature-document-fields" className="surface-panel">
			<div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
				<h3 className="text-sm font-semibold text-foreground">{DocumentosLabels.sendFields}</h3>
				{pending && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
			</div>

			<div className="flex flex-col gap-5 p-4">
				{terms.map((term) => {
					const requirement = requirements.find((candidate) => candidate.termId === term.id);
					const fields = requirement?.fields ?? [];
					const filled = fields.filter((field) => {
						const key = SignaturePreparation.fieldKey(term.id, field.fieldId);

						return SignaturePreparation.valid(field, customValues[key]);
					}).length;

					return (
						<div
							key={term.id}
							data-slot="term-fields"
							data-term-id={term.id}
							data-filled={filled}
							data-total={fields.length}
						>
							<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
								<p className="text-sm font-medium text-foreground">{term.name}</p>

								{fields.length > 0 && (
									<p className="text-xs text-muted-foreground">
										{DocumentosLabels.preparation.fieldProgress(filled, fields.length)}
									</p>
								)}
							</div>

							{fields.length === 0 && (
								<div className="flex items-center gap-2 rounded-md bg-whatsapp/10 px-3 py-2 text-xs text-whatsapp">
									<CircleCheck className="size-4" />
									{DocumentosLabels.preparation.automaticFields}
								</div>
							)}

							{fields.length > 0 && (
								<div className="grid gap-3 sm:grid-cols-2">
									{fields.map((field) => (
										<SendTermsFieldInput
											key={field.fieldId}
											field={field}
											name={
												`customValues.${SignaturePreparation.fieldKey(term.id, field.fieldId)}` as FieldPath<SignaturePreparationForm>
											}
											control={control}
											disabled={disabled}
										/>
									))}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</section>
	);
}
